/**
 * Subscriptions — request schemas + wire types (docs/SUBSCRIPTIONS.md).
 *
 * Served by `apps/api/src/routes/subscriptions.ts` over migration
 * `20261030120000_subscriptions.sql`:
 *
 *   GET  /api/v1/subscriptions/orgs/:orgId            → SubscriptionOverviewResponse (members)
 *   GET  /api/v1/subscriptions/orgs/:orgId/features   → OrganizationFeatureWire[] (members)
 *   POST /api/v1/subscriptions/orgs/:orgId/checkout   → { url } (org admins)
 *   POST /api/v1/subscriptions/orgs/:orgId/portal     → { url } (org admins; optional {plan, interval} = switch)
 *   GET  /api/v1/subscriptions/admin                  → { items: SubscriptionAdminRow[] } (super admin)
 *   PUT  /api/v1/subscriptions/admin/:orgId           → SubscriptionStateWire (super admin)
 *   GET  /api/v1/subscriptions/admin/features         → { items: FeatureMatrixRowWire[] } (super admin)
 *
 * The wire interfaces are shared by the API handlers and the web hooks so the
 * two sides cannot drift.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** Billing intervals. A Stripe Price's `lookup_key` is `<plan_code>_<interval>`. */
export const SUBSCRIPTION_INTERVALS = ["monthly", "annual"];
/** `subscription_org_state.source`. */
export const SUBSCRIPTION_SOURCES = ["trial", "stripe", "manual"];
/** Stripe statuses under which a `source = 'stripe'` plan is live (the SQL rule's stripe arm). */
export const LIVE_STRIPE_STATUSES = ["active", "trialing", "past_due"];
/**
 * Whether a subscription row is a live Stripe subscription — `source = 'stripe'`
 * under a live status (the SQL rule's stripe arm). The one copy, shared by the
 * API's Checkout refusal and the web's "Manage billing is the path" check.
 */
export function isLiveStripeState(state) {
    return state?.source === "stripe" && LIVE_STRIPE_STATUSES.includes(state.status);
}
/** The currencies a plan's display price is offered in (EUR home, USD `currency_options`). */
export const SUBSCRIPTION_PRICE_CURRENCIES = ["EUR", "USD"];
/** The Price lookup key of a plan + interval — the ONLY use of lookup keys (display prices, Checkout's line item). */
export function subscriptionPriceLookupKey(planCode, interval) {
    return `${planCode}_${interval}`;
}
/** The refusal `code`s the API answers with; the web keys its copy on these. */
export const SUBSCRIPTION_ERROR_CODES = {
    billingUnconfigured: "billing_unconfigured",
    stripeSubscriptionLive: "stripe_subscription_live",
    noStripeCustomer: "no_stripe_customer",
    /** A super admin starting Checkout for an org they do not admin must name the customer's email. */
    customerEmailRequired: "customer_email_required",
    /** Only a super admin may name the customer's email (an org admin's own email is used). */
    customerEmailSuperAdminOnly: "customer_email_super_admin_only",
    /** A plan switch (portal with `{plan, interval}`) needs a live Stripe subscription. */
    noLiveStripeSubscription: "no_live_stripe_subscription",
    /** A plan switch to the price the subscription already pays. */
    alreadyOnPrice: "already_on_price",
    /** A plan switch on a subscription with more than one item (the portal flow swaps one). */
    subscriptionHasMultipleItems: "subscription_has_multiple_items",
    /** The Stripe Portal configuration has "Subscription update" disabled (our setup, not the user's). */
    portalSwitchingDisabled: "portal_switching_disabled",
};
// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------
/**
 * POST …/checkout. `plan` must be a self-serve plan code (checked against the DB).
 * `customer_email` is the super-admin "set them up" path (not the happy path):
 * the email Stripe puts on the new customer when a super admin starts Checkout
 * on an org's behalf. Org admins never send it — their own email is used.
 */
export const subscriptionCheckoutSchema = z
    .object({
    plan: z.string().trim().min(1).max(100),
    interval: z.enum(SUBSCRIPTION_INTERVALS),
    customer_email: z.string().trim().email().max(254).optional(),
})
    .strict();
/**
 * POST …/portal. Empty (`{}` or no body) → the plain Billing Portal. With
 * `{plan, interval}` (both or neither) → the portal opened straight on the
 * "confirm this change" page for a live Stripe subscription switching to that
 * plan's price for that interval (`subscription_update_confirm` flow),
 * returning to the settings page with `?switched=<target price id>`.
 */
export const subscriptionPortalSchema = z
    .object({
    plan: z.string().trim().min(1).max(100),
    interval: z.enum(SUBSCRIPTION_INTERVALS),
})
    .partial()
    .strict()
    .refine((v) => (v.plan === undefined) === (v.interval === undefined), {
    message: "plan and interval go together",
    path: ["interval"],
});
/**
 * PUT /admin/:orgId — super admin assigns a manual plan (optional end) or
 * starts / extends a trial (`trial_ends_at` required, in the future; the SQL
 * re-checks). Dates are ISO instants (timestamptz).
 */
export const subscriptionAdminSetSchema = z
    .object({
    plan_code: z.string().trim().min(1).max(100),
    source: z.enum(["manual", "trial"]),
    trial_ends_at: z.string().datetime({ offset: true }).nullish(),
    manual_ends_at: z.string().datetime({ offset: true }).nullish(),
})
    .strict()
    .refine((v) => v.source !== "trial" || !!v.trial_ends_at, {
    message: "trial_ends_at is required for a trial",
    path: ["trial_ends_at"],
});
/** Which arm of the resolution rule decided a feature (`subscription_feature_states.source`). */
export const FEATURE_STATE_SOURCES = ["override", "plan", "default"];
//# sourceMappingURL=subscriptions.js.map